/**
 * Preferences Service
 *
 * Fetches and updates notification preferences via the Gateway Coral client.
 * The Gateway proxies to JwoNotificationService GetPreferences / UpdatePreferences.
 */

import { getServiceClient } from '@api/client-factory'
import { GetPreferencesInput } from '@amzn/coral_com-amazon-jwonotificationservice/GetPreferencesInput'
import { UpdatePreferencesInput } from '@amzn/coral_com-amazon-jwonotificationservice/UpdatePreferencesInput'

export interface INotificationPreferences {
    pushEnabled: boolean
    smsEnabled: boolean
    emailEnabled: boolean
    quietTimeEnabled: boolean
    criticalAlertsEnabled: boolean
}

export class PreferencesService {
    /**
     * Fetches notification preferences for the current user.
     * Calls Gateway GetNotificationPreferences which proxies to NS.
     */
    public static async getPreferences (): Promise<INotificationPreferences> {
        const output = await getServiceClient()
            .getNotificationPreferences(GetPreferencesInput.fromJson({}))
            .toPromise()

        return {
            pushEnabled: output.push?.enabled ?? false,
            smsEnabled: output.sms?.enabled ?? false,
            emailEnabled: output.email?.enabled ?? false,
            quietTimeEnabled: output.quietTime ?? false,
            criticalAlertsEnabled: output.criticalAlerts ?? false,
        }
    }

    /**
     * Updates notification preferences for the current user.
     * Calls Gateway UpdateNotificationPreferences which proxies to NS.
     * Only sends fields that are being changed; omitted fields remain unchanged on the backend.
     */
    public static async updatePreferences (
        updates: Partial<INotificationPreferences>,
    ): Promise<INotificationPreferences> {
        const input: Record<string, unknown> = {}

        if (updates.quietTimeEnabled !== undefined) {
            input.quietTime = updates.quietTimeEnabled
        }
        if (updates.criticalAlertsEnabled !== undefined) {
            input.criticalAlerts = updates.criticalAlertsEnabled
        }
        if (updates.pushEnabled !== undefined) {
            input.push = { enabled: updates.pushEnabled }
        }
        if (updates.smsEnabled !== undefined) {
            input.sms = { enabled: updates.smsEnabled, method: 'APP_SETTINGS' }
        }
        if (updates.emailEnabled !== undefined) {
            input.email = { enabled: updates.emailEnabled, method: 'APP_SETTINGS' }
        }

        const output = await getServiceClient()
            .updateNotificationPreferences(UpdatePreferencesInput.fromJson(input))
            .toPromise()

        return {
            pushEnabled: output.push?.enabled ?? false,
            smsEnabled: output.sms?.enabled ?? false,
            emailEnabled: output.email?.enabled ?? false,
            quietTimeEnabled: output.quietTime ?? false,
            criticalAlertsEnabled: output.criticalAlerts ?? false,
        }
    }
}
